import type { tr } from './tr.ts';

// English interface strings. The key set is enforced against `tr.ts` at
// compile time: a missing translation fails `npm run typecheck`.

export const en: Record<keyof typeof tr, string> = {
  // --- Page meta -------------------------------------------------------------
  'meta.description': 'One player for YouTube videos, live TV channels and your own M3U/Xtream playlist. No channel or content packages are sold. Built-in channels have no time limit; YouTube is 30 minutes a day on the Free plan.',
  'meta.home': 'YouTube, live TV and your IPTV list in one player',
  'meta.notFound': 'Page not found',
  'meta.compat': 'Is your device supported?',
  'meta.legal': 'Legal',
  'legal.privacy': 'Privacy Policy',
  'legal.subscription': 'Subscription and Cancellation',
  'legal.contact': 'Contact',

  // --- Language --------------------------------------------------------------
  'lang.label': 'Language',
  'lang.lead': 'The interface language is stored on this device only and does not affect your other devices.',
  'lang.tr': 'Türkçe',
  'lang.en': 'English',
  'lang.switch': 'Language',

  // --- Common ----------------------------------------------------------------
  'common.back': 'Go back',
  'common.retry': 'Try again',
  'common.cancel': 'Cancel',
  'common.loading': 'Loading…',
  'common.loadMore': 'Show more',
  'common.all': 'All',
  'common.other': 'Other',
  'cat.national': 'National',
  'cat.news': 'News',
  'cat.sports': 'Sports',
  'cat.documentary': 'Documentary',
  'cat.music': 'Music',
  'cat.kids': 'Kids',
  'common.space': 'Space',
  'common.error': 'An error occurred.',
  'common.network': 'Could not connect. Please check your internet connection.',
  'common.never': 'not yet',
  'common.justNow': 'just now',
  'common.minutesAgo': '{n} min ago',
  'common.hoursAgo': '{n} h ago',
  'common.daysAgo': '{n} d ago',
  'common.viewPlans': 'View plans',
  'common.goSettings': 'Go to Settings',
  'common.goDiscover': 'Back to Discover',

  // --- Device labels ---------------------------------------------------------
  'device.car': 'Car display',
  'device.tv': 'Television',
  'device.tablet': 'Tablet',
  'device.phone': 'Phone',
  'device.desktop': 'Computer',

  // --- App shell -------------------------------------------------------------
  'nav.discover': 'Discover',
  'nav.live': 'Live',
  'nav.mylist': 'My List',
  'nav.iptv': 'IPTV',
  'nav.account': 'Settings',
  'nav.plans': 'Plans',
  'nav.settings': 'Settings',
  'nav.logout': 'Sign out',
  'nav.search': 'Search',
  'nav.searchPlaceholder': 'Search content',
  'nav.mainNav': 'Main navigation',
  'nav.sections': 'Sections',
  'nav.home': 'Home',
  'nav.accountMenu': 'Account menu',
  'nav.accountMenuTitle': 'Account, plans and settings',
  'nav.myAccount': 'My account',
  'nav.plusOnly': 'Included in the Plus plan',
  'nav.lockedLabel': '{label} — included in the Plus plan',
  'nav.admin': 'Statistics',

  // --- Statistics (admin) ---
  'admin.title': 'Usage statistics',
  'admin.sub': 'Who watched what, from which device, at which quality, for how long; every action in the app.',
  'admin.range.today': 'Today',
  'admin.range.7': '7 days',
  'admin.range.30': '30 days',
  'admin.range.90': '90 days',
  'admin.range.365': '1 year',
  'admin.kpi.activeUsers': 'Active users',
  'admin.kpi.totalUsers': 'Total accounts',
  'admin.kpi.newUsers': 'New accounts',
  'admin.kpi.watchTime': 'Watch time',
  'admin.kpi.sessions': 'Playback sessions',
  'admin.kpi.avgSession': 'Average session',
  'admin.kpi.bytes': 'Data transferred',
  'admin.kpi.allTime': 'Total watch time (all time)',
  'admin.nowPlaying': 'Watching now',
  'admin.nowPlayingEmpty': 'No stream is open right now.',
  'admin.byKind': 'By content type',
  'admin.byQuality': 'By quality',
  'admin.byDevice': 'By device',
  'admin.byPlan': 'By plan',
  'admin.byDay': 'By day',
  'admin.byHour': 'By hour of day (Istanbul)',
  'admin.topContent': 'Most watched',
  'admin.endReasons': 'Stream end reasons',
  'admin.eventCounts': 'Action counts',
  'admin.users': 'Users',
  'admin.users.search': 'Search e-mail or name',
  'admin.users.total': 'Accounts: {n}',
  'admin.events': 'Recent actions',
  'admin.loadMore': 'Show more',
  'admin.user.back': 'All users',
  'admin.plan.title': 'Plan management',
  'admin.plan.listHint': 'Select a user’s email address to change their plan.',
  'admin.plan.current': 'Current plan: {plan}',
  'admin.plan.ends': 'Ends: {date}',
  'admin.plan.noExpiry': 'No expiry date',
  'admin.plan.hint': 'The selected plan applies immediately, without a charge or expiry date. Downgrading from Plus stops any active IPTV stream.',
  'admin.plan.save': 'Apply plan',
  'admin.plan.saved': 'Plan updated to {plan}.',
  'admin.event.admin_plan_changed': 'Admin plan change',
  'admin.user.sessions': 'Playback sessions',
  'admin.user.events': 'Action log',
  'admin.user.devices': 'Devices',
  'admin.user.created': 'Registered',
  'admin.loadFail': 'Statistics could not be loaded.',
  'admin.forbidden': 'This section is available to administrators only.',
  'admin.empty': 'No records in this range.',
  'admin.deleted': 'Deleted account',
  'admin.col.user': 'User',
  'admin.col.plan': 'Plan',
  'admin.col.watch': 'Watch time',
  'admin.col.sessions': 'Sessions',
  'admin.col.devices': 'Devices',
  'admin.col.lastSeen': 'Last seen',
  'admin.col.created': 'Registered',
  'admin.col.day': 'Day',
  'admin.col.users': 'Users',
  'admin.col.title': 'Title',
  'admin.col.kind': 'Type',
  'admin.col.time': 'Time',
  'admin.col.event': 'Action',
  'admin.col.detail': 'Detail',
  'admin.col.device': 'Device',
  'admin.col.quality': 'Quality',
  'admin.col.reason': 'Reason',
  'admin.col.count': 'Count',
  'admin.col.stalls': 'Stalls',
  'admin.col.started': 'Started',
  'admin.unit.hours': '{h} h {m} min',
  'admin.unit.minutes': '{m} min',
  'admin.unit.seconds': '{s} s',
  'admin.quality.source': 'Source quality',
  'admin.kind.youtube-vod': 'YouTube video',
  'admin.kind.youtube-live': 'YouTube live',
  'admin.kind.youtube': 'YouTube',
  'admin.kind.tv': 'Built-in channel',
  'admin.kind.iptv-live': 'IPTV live',
  'admin.kind.iptv-vod': 'IPTV film/series',
  'admin.kind.iptv': 'IPTV',
  'admin.kind.test': 'Test media',
  'admin.kind.unknown': 'Unknown',
  'admin.event.view': 'Page view',
  'admin.event.play_start': 'Playback started',
  'admin.event.play_end': 'Playback ended',
  'admin.event.play_error': 'Playback error',
  'admin.event.player_error': 'Player error',
  'admin.event.pause': 'Pause',
  'admin.event.resume': 'Resume',
  'admin.event.stall': 'Stall',
  'admin.event.net_slow': 'Freeze diagnosis',
  'admin.net.client': 'Viewer connection slow',
  'admin.net.source': 'Source or server slow',
  'admin.net.gate': '{n} s loading screen',
  'admin.net.backlog': '{n} KB in transit',
  'admin.net.clientCount': 'Connection-related {n}',
  'admin.net.sourceCount': 'Source-related {n}',
  'admin.event.seek': 'Seek',
  'admin.event.skip': 'Skip',
  'admin.event.quality_change': 'Quality change',
  'admin.event.fullscreen': 'Full screen',
  'admin.event.related_open': 'Related shelf',
  'admin.event.takeover': 'Device takeover',
  'admin.event.search': 'Search',
  'admin.event.favorite_add': 'Favourite added',
  'admin.event.favorite_remove': 'Favourite removed',
  'admin.event.source_add': 'Source added',
  'admin.event.source_remove': 'Source removed',
  'admin.event.login': 'Sign-in',
  'admin.event.register': 'Registration',
  'admin.event.logout': 'Sign-out',
  'admin.event.password_change': 'Password change',
  'admin.event.password_reset_request': 'Password reset request',
  'admin.event.password_reset': 'Password reset',
  'admin.event.account_delete': 'Account deletion',
  'admin.event.device_remove': 'Device removed',
  'admin.event.checkout_start': 'Checkout started',
  'admin.event.purchase': 'Purchase',
  'admin.event.subscription_cancel': 'Subscription cancelled',
  'admin.event.subscription_resume': 'Subscription resumed',
  'admin.billing.title': 'Subscriptions and revenue',
  'admin.billing.testNote': 'Subscription, failed-payment and cancellation counts include sandbox records; revenue figures do not. In the sandbox: {n} subscriptions, monthly equivalent {mrr}, charges in this range {collected}.',
  'admin.billing.paying': 'Paying subscriptions',
  'admin.billing.trial': 'In trial',
  'admin.billing.unpaid': 'Payment failed',
  'admin.billing.cancelled': 'Cancelled (still active)',
  'admin.billing.mrr': 'Monthly recurring revenue',
  'admin.billing.collected': 'Collected in this range',
  'admin.billing.failed': 'Failed payments',
  'admin.billing.cancellations': 'Cancellations',
  'admin.billing.payments': 'Recent transactions',
  'admin.billing.none': 'No payment transactions in this range.',
  'admin.billing.deletedAccount': 'Deleted account',
  'admin.billing.col.period': 'Period',
  'admin.billing.col.subs': 'Subscriptions',
  'admin.billing.col.test': 'Sandbox',
  'admin.billing.col.kind': 'Transaction',
  'admin.billing.col.amount': 'Amount',
  'admin.billing.kind.trial_started': 'Trial started',
  'admin.billing.kind.purchase': 'Purchase',
  'admin.billing.kind.renewal': 'Renewal',
  'admin.billing.kind.payment_failed': 'Payment failed',
  'admin.billing.kind.cancelled': 'Cancellation',
  'admin.billing.kind.plan_changed': 'Plan change',
  'admin.billing.kind.card_updated': 'Card update',
  'admin.billing.kind.other': 'Other',
  'admin.event.plan_change': 'Plan change',
  'admin.event.card_update': 'Card update',
  'admin.event.checkout_failed': 'Payment not completed',
  'admin.event.purchase_failed': 'Renewal payment failed',
  'admin.reason.client_stopped': 'Closed by user',
  'admin.reason.client_closed': 'Tab closed',
  'admin.reason.source_ended': 'Content ended',
  'admin.reason.quota_exhausted': 'Quota exhausted',
  'admin.reason.taken_over': 'Taken over by another device',
  'admin.reason.preempted': 'Capacity: made room for a paid account',
  'admin.reason.lease_expired': 'Connection lost',
  'admin.reason.entitlement_lost': 'Plan entitlement lost',
  'admin.reason.start_failed': 'Failed to start',
  'admin.reason.server_shutdown': 'Server restarted',
  'admin.reason.source_deleted': 'Source deleted',
  'admin.reason.account_deleted': 'Account deleted',
  'admin.reason.stopped': 'Stopped',
  'quota.exhausted': 'YouTube time used up',
  'quota.secondsLeft': 'YouTube: {n} sec left',
  'quota.minutesLeft': 'YouTube: {n} min left',
  'quota.title': 'The Free plan includes a total of {n} minutes of YouTube playback per day. Built-in channels do not count towards it.',

  // --- Landing ---------------------------------------------------------------
  'landing.nav.experience': 'Experience',
  'landing.nav.plans': 'Plans',
  'landing.nav.login': 'Sign in',
  'landing.cta.startFree': 'Start for free',
  'landing.hero.title1': 'What you want to watch.',
  'landing.hero.title2': 'Wherever you want to be.',
  'landing.hero.lead': 'YouTube videos, live TV channels and your own IPTV playlist in one player. On car displays, phones and computers.',
  'landing.hero.noteFree': 'Channels unlimited, YouTube 30 min a day. No card needed.',
  'landing.showcase.sample': 'Sample view',
  'landing.scene.alt': 'Coastal road and a sailboat — original sample illustration',
  'landing.scene.tag': 'Step away for a while.',
  'landing.scene.title1': 'Beyond',
  'landing.scene.title2': 'the shore.',
  'landing.scene.body1': 'A new view, another story.',
  'landing.scene.body2': 'Company for the time you set aside for yourself.',
  'landing.scene.cta': 'Explore the interface',
  'landing.shelf.1': 'A day up high',
  'landing.shelf.2': 'Another face of the city',
  'landing.shelf.3': 'The last light of day',
  'landing.shelf.meta': 'Sample content · {n} min left',
  'landing.caption.1': 'One screen. A calm viewing experience.',
  'landing.caption.2': 'Design demonstration; content shown is sample material.',
  'landing.device.phone': 'Phone',
  'landing.device.tablet': 'Tablet',
  'landing.device.pc': 'Computer',
  'landing.device.tv': 'TV browser',
  'landing.device.compat': 'Compatibility status',
  'landing.feature.title1': 'Your own list.',
  'landing.feature.title2': 'Organised your way.',
  'landing.feature.body': 'Add your IPTV source with the Plus plan. Find your channels, movies and series in one place; return to your favourites and continue where you left off.',
  'landing.feature.link': 'See the Plus plan',
  'landing.source.head': 'My sources',
  'landing.source.title': 'Everything in its place.',
  'landing.source.body': 'Content comes from the provider you add.',
  'landing.source.row1': 'Live channels',
  'landing.source.row1meta': 'By category',
  'landing.source.row2': 'Movies and series',
  'landing.source.row2meta': 'In one list',
  'landing.source.row3': 'Your favourites',
  'landing.source.row3meta': 'Easy access',
  'landing.pillars.title1': 'Three sources.',
  'landing.pillars.title2': 'One player.',
  'landing.pillar.1.title': 'Search and discover on YouTube',
  'landing.pillar.1.body': 'Find the video you are looking for, explore by topic and continue where you left off.',
  'landing.pillar.2.title': 'Built-in live channels',
  'landing.pillar.2.body': 'National, news, music and documentary channels open with a single tap; no list is required and there is no time limit.',
  'landing.pillar.3.title': 'Your own IPTV list',
  'landing.pillar.3.body': 'Add your M3U or Xtream source with the Plus plan; your channels, movies and series in the same place.',
  'landing.car.tag': 'Car display',
  'landing.car.title1': 'On your car’s display,',
  'landing.car.title2': 'with nothing to install.',
  'landing.car.body': 'Designed for Tesla and similar in-car browsers: nothing to install, just open the address. The interface is sized to be read at arm’s length and tapped comfortably inside the car.',
  'landing.car.point1': 'Large touch targets and legible type',
  'landing.car.point2': 'A player that fits low, wide screens',
  'landing.car.point3': 'Full screen while parked, where the vehicle allows it',
  'landing.car.safety': 'Do not watch video while driving. The screen is for passengers.',
  'landing.car.devices': 'The same account also opens on:',
  'landing.closing.title': 'Try it free today.',
  'landing.closing.body': 'Creating an account is free and no card details are required. You can watch the built-in channels with no time limit and YouTube videos for 30 minutes every day.',
  'landing.faq.q5': 'What do I gain with yearly payment?',
  'landing.faq.a5': 'With yearly payment you pay for 10 months and use the plan for 12; two months are free. The trial period is the same for monthly and yearly payment.',
  'landing.faq.q6': 'How do I use it on a car display?',
  'landing.faq.a6': 'Open the address in the in-car browser and sign in; nothing is installed. In the Tesla browser, full screen is available only in Theatre mode while the vehicle is parked; the full-screen button in the player shows the step this requires. Behaviour may vary by vehicle model and software version.',
  'landing.faq.title1': 'Frequently asked',
  'landing.faq.title2': 'questions.',
  'landing.faq.q1': 'What is included in the Free plan?',
  'landing.faq.a1': 'You can watch the built-in live channels with no time limit. YouTube videos are limited to a total of 30 minutes each day. Personal IPTV is not included in the Free plan.',
  'landing.faq.q2': 'Does Plus include a ready-made IPTV subscription?',
  'landing.faq.a2': 'No. Plus unlocks the player features that let you add your own M3U or Xtream source. No channel or content packages are sold.',
  'landing.faq.q3': 'Can I watch on more than one device?',
  'landing.faq.a3': 'You can use your account on different devices. Only one stream can be open at a time; this limit is the same on every plan.',
  'landing.faq.q4': 'Which devices are supported?',
  'landing.faq.a4': 'Car, phone, tablet, computer and TV browsers are targeted. Compatibility with specific models and browsers is still being verified; this release does not guarantee compatibility.',
  'landing.footer.tagline': 'Make a little room for watching.',
  'landing.plans.loadFail': 'Plan information could not be loaded right now. Refresh the page and try again.',

  // --- Plans -----------------------------------------------------------------
  'plans.intro.title1': 'Start with the plan',
  'plans.intro.title2': 'that suits you.',
  'plans.intro.body1': 'One stream at a time on every plan.',
  'plans.intro.body2': 'Personal IPTV access is available only on Plus.',
  'plans.fine.vat': 'Prices include VAT.',
  'plans.fine.limits': 'Unlimited use is not promised on any plan; a fair-use policy applies and any limits are announced on this page in advance.',
  'plans.fine.noIptv': 'No IPTV subscription or content package is sold; you need to add your own source.',
  'plans.fine.mock': 'No payment provider is connected yet: the purchase step is a test flow and no money is charged.',
  'plans.fine.real': 'Payments are processed by iyzico; your card details are not stored on this site. The amount and renewal period are shown again at the payment step.',
  'plans.fine.sandbox': 'TEST environment: payments run on iyzico’s sandbox; no real charge is made.',
  'plans.fine.offline': 'Online payment is not currently available; plan changes are handled through the address in the {contact} section.',
  'plans.contactToUpgrade': 'To change your plan, write to the address in the {contact} section.',
  'plans.swipe': 'Swipe sideways to see all plans',
  'plans.free': '0 TL',
  'plans.perFirstMonth': ' TL / first month',
  'plans.perMonth': ' TL / month',
  'plans.perYear': ' TL / year',
  'plans.period.label': 'Billing period',
  'plans.period.monthly': 'Monthly',
  'plans.period.yearly': 'Yearly',
  'plans.period.badge': '2 months free',
  'plans.renew.yearly': 'Equivalent to {price} per month.',
  'plans.renew.yearlyNote': '{n} months free; {saving} saved per year.',
  'plans.createAccount': 'Create an account',
  'plans.checkout.yearly': '{price} per year · renews every 365 days',
  'pay.periodValueYearly': '365 days',
  'plans.renew.free': '30 minutes of YouTube every day',
  'plans.renew.intro': 'Then {price}.',
  'plans.renew.introNote': 'New members only.',
  'plans.renew.flat': 'Same price from the first month.',
  'plans.renew.flatNote': 'You can cancel at any time.',
  'plans.renew.trial': 'Free for the first {n} days.',
  'plans.renew.trialNote': 'Then {price}; free to cancel in the trial.',
  'plans.renew.yearlyTrial': 'First {n} days free · equals {price} per month.',
  'plans.current': 'Your current plan',
  'plans.backToFree': 'To return to the Free plan, cancel your subscription.',
  'plans.cancelFirst': 'To change plans, cancel your current subscription first. A new plan starts on the day you buy it; the remaining time of the current period is not carried over.',
  'plans.changeTo': 'Switch to {name}',
  'plans.changeTitle': 'Switching to {name}',
  'plans.changeTrial': 'You move to the {name} plan now. Your trial is kept and {price} is charged to your card when it ends. The exact date is shown under Your subscription after the change.',
  'plans.changeUp': 'The {name} plan is available immediately and you are not charged extra for this period. From {date}, {price} is charged.',
  'plans.changeDown': 'You keep your current plan until {date}. From that date you move to the {name} plan and {price} is charged.',
  'plans.changeConfirm': 'Change plan',
  'plans.changedNow': 'You have moved to the {name} plan.',
  'plans.changedLater': 'Your plan change has been recorded; it takes effect on {date}.',
  'plans.changePending': 'You will move to the {name} plan in the next period. A new change can be made after that date.',
  'plans.changePendingThis': 'You will move to this plan from {date}.',
  'plans.periodChange': 'To change the billing period (monthly or yearly), cancel your current subscription first. A new subscription starts on the day you buy it; the remaining time of the current period is not added.',
  'plans.resubscribe': 'Subscribe again',
  'plans.unpaidFirst': 'Your last payment could not be taken. You can update your card or cancel your subscription below.',
  'plans.switchTitle': 'The new subscription starts today',
  'plans.switchBody': 'You are entitled to use your current plan until {date}. If you start a new subscription now, you are charged today and the period counts from today; the remaining time of your current period is not added.',
  'plans.switchConfirm': 'Continue',
  'plans.continueFree': 'Continue for free',
  'plans.startFree': 'Start for free',
  'plans.buyOnPhone': 'Buy from your phone',
  'plans.choose': 'Choose {name}',
  'plans.checkout.title': '{name} plan',
  'plans.checkout.monthly': '{price} per month · renews every 30 days',
  'plans.checkout.first': 'First payment {first} · then {renew}',
  'plans.checkout.test': 'No payment provider is connected. No card details are requested and nothing is charged.',
  'plans.checkout.confirmTest': 'Proceed to test payment',
  'plans.checkout.confirm': 'Proceed to payment',
  'plans.checkout.fail': 'The purchase could not be started.',
  'plans.page.title': 'Plans',
  'plans.page.sub': 'One stream at a time on every plan.',
  'plans.page.loadFail': 'Plans could not be loaded',
  'plans.name.free': 'Free',
  'plans.name.starter': 'Starter',
  'plans.name.plus': 'Plus',

  // --- Subscription ----------------------------------------------------------
  'sub.section': 'Your subscription',
  'sub.active': 'Active plan',
  'sub.expiredSuffix': ' · expired',
  'sub.renewal': 'Renewal',
  'sub.noRenewal': 'No renewal',
  'sub.concurrent': 'Simultaneous streams',
  'sub.concurrentValue': '{n} — the same on every plan',
  'sub.iptv': 'Personal IPTV',
  'sub.on': 'Enabled',
  'sub.off': 'Disabled · requires Plus',
  'sub.endingTitle': 'Subscription ends at the end of the period',
  'sub.endingBody': 'You will return to the Free plan at the end of the period.',
  'sub.endingNoResume': 'The subscription was cancelled; you can keep using it until the end of the period. To subscribe again, choose a plan.',
  'sub.resume': 'Undo cancellation',
  'sub.title': 'Subscription',
  'sub.cancelBody': 'If you cancel, you keep every feature until the end of the current period.',
  'sub.cancel': 'Cancel subscription',
  'sub.cancelDialogTitle': 'Cancel subscription',
  'sub.cancelDialogBody': 'You will return to the Free plan at the end of the period. All features remain available until then.',
  'sub.cancelConfirm': 'Cancel subscription',
  'sub.keep': 'Keep subscription',
  'sub.cancelled': 'The subscription will end at the end of the period.',
  'sub.cancelledNow': 'The subscription was cancelled; your account has returned to the Free plan.',
  'sub.cancelDialogBodyNow': 'Because your paid period has already ended, your access ends as soon as you cancel and your account returns to the Free plan.',
  'sub.resumed': 'Cancellation undone.',
  'sub.pendingPlan': 'Next period',
  'sub.pendingPlanValue': '{plan} · from {date}',
  'sub.cardTitle': 'Payment card',
  'sub.cardBody': 'Your card is stored by the payment provider iyzico; card details are not kept on this site and cannot be shown. You can change your card in iyzico’s form.',
  'sub.cardUpdate': 'Update card',
  'sub.cardDone': 'The card update form has been completed. If your card was accepted, it is used for the next charge.',
  'sub.cardUnknown': 'You returned from the card update step, but its result could not be verified. Your card may not have changed; you can try again to be sure.',
  'sub.cardFailed': 'The card could not be updated. You can try again.',
  'sub.ev.plan_changed': '{plan} · plan change',
  'sub.ev.card_updated': 'Payment card update form completed',
  'sub.trialTag': 'Trial period',
  'sub.period': 'Billing period',
  'sub.trialEnds': 'Trial ends and first charge',
  'sub.nextCharge': 'Next charge',
  'sub.chargeValue': '{date} · {price}',
  'sub.accessEnds': 'Access ends',
  'sub.accessEndsValue': '{date} · no further charge',
  'sub.planEnds': 'Plan ends',
  'sub.noEnd': 'No end date',
  'sub.endedTitle': 'Your subscription has ended',
  'sub.endedBody': 'You are on the Free plan. You can subscribe again by choosing a plan above.',
  'sub.unpaidTitle': 'Your last payment could not be taken',
  'sub.unpaidBody': 'The renewal charge could not be taken from your card. The payment provider may retry; if it succeeds, your subscription continues without interruption. Otherwise your access ends on {date}. You can update your card below.',
  'sub.unpaidEndedBody': 'Your plan was closed because the renewal charge could not be taken. To subscribe again, cancel this subscription first, then choose a plan.',
  'sub.history': 'Transaction history',
  'sub.historyLead': 'Your purchases, charges and subscription changes. These records are not invoices.',
  'sub.historyEmpty': 'You have no transactions yet.',
  'sub.historyFail': 'The transaction history could not be loaded.',
  'sub.historyRef': 'Transaction no. {ref}',
  'sub.noCharge': 'Not charged',
  'sub.ev.trial_started': '{plan} · trial started',
  'sub.ev.purchase': '{plan} · purchase',
  'sub.ev.renewal': '{plan} · renewal',
  'sub.ev.payment_failed': '{plan} · payment failed',
  'sub.ev.cancelled': '{plan} · subscription cancelled',
  'sub.ev.resumed': '{plan} · cancellation undone',
  'sub.ev.admin': '{plan} · plan assigned',
  'sub.ev.other': '{plan} · transaction',
  'addons.title': 'Add-ons',
  'addons.body': 'Add-ons work together with the plan they belong to; when the base plan ends, the add-on ends too.',
  'addons.requires': 'requires the {plan} plan',

  // --- Test payment ----------------------------------------------------------
  'pay.title': 'Payment',
  'pay.plan': '{name} plan',
  'pay.testNote': ' No payment provider is connected yet. This is not a real payment page: no card details are requested, nothing is charged, and no request is sent to any provider.',
  'pay.package': 'Plan',
  'pay.period': 'Period',
  'pay.periodValue': '30 days',
  'pay.charge': 'Charge',
  'pay.chargeValue': 'None — this is a test record, not a real subscription',
  'pay.record': 'Transaction record',
  'pay.recordValue': 'Marked as test',
  'pay.activate': 'Activate test subscription',
  'pay.processing': 'Processing…',
  'bill.title': 'Billing details',
  'bill.lead': 'The subscription is opened through the payment provider iyzico, which requires these details. They are sent only to iyzico and are not stored on this site.',
  'bill.name': 'First name',
  'bill.surname': 'Last name',
  'bill.identity': 'Turkish ID number',
  'bill.phone': 'Mobile phone',
  'bill.phonePh': '+90 5xx xxx xx xx',
  'bill.city': 'City',
  'bill.address': 'Billing address',
  'bill.submit': 'Continue to payment',
  'bill.opening': 'Opening the payment page…',
  'bill.invalid': 'Enter all details correctly: an 11-digit ID number and a phone number with its code.',
  'bill.cardNote': 'You will enter your card details in iyzico’s form in the next step.',
  'bill.formTitle': 'Billing contact',
  'bill.identityHint': 'Required by iyzico, which opens the subscription in a person’s name.',
  'bill.stepsLabel': 'Payment steps',
  'bill.step1': 'Billing details',
  'bill.step2': 'Card details',
  'bill.step3': 'Confirmation',
  'bill.summaryTitle': 'Order summary',
  'bill.sumPlan': 'Plan',
  'bill.sumPeriod': 'Billing period',
  'bill.monthly': 'Monthly',
  'bill.yearly': 'Yearly',
  'bill.sumAfterTrial': 'After {n} days',
  'bill.sumToday': 'Due today',
  'bill.dueTrial': 'No charge',
  'bill.assureCard': 'Your card details are entered in iyzico’s form; they do not reach this site and are not stored here.',
  'bill.assureProvider': 'Payments are processed by iyzico.',
  'bill.assureCancel': 'You can cancel your subscription at any time from the Plans screen.',
  'bill.assureCreditCard': 'Subscription payments are taken by credit card only; debit cards are not accepted.',
  'bill.consentLink': 'I have read the subscription and cancellation terms.',
  'bill.consent': 'I want the service to start immediately; I therefore accept that I have no right of withdrawal and that amounts charged are not refunded.',
  'bill.consentRequired': 'Please accept the subscription terms to continue.',
  'bill.assureNoRefund': 'After cancelling you keep access until the end of the period; unused time is not refunded.',
  'pay.success': 'Your subscription has started.',
  'pay.failed': 'The payment could not be completed; no subscription was started.',
  'pay.expired': 'The payment session has expired. Please try again.',
  'pay.verifying': 'Verifying your payment…',
  'pay.pending': 'Your payment could not be verified yet. If your plan is not active within a few minutes, reopen this screen; if the problem continues, write to us at the Contact address.',
  'done.title': 'Your subscription has started',
  'done.leadTrial': 'Your {plan} plan is now active with a trial period. You have not been charged today.',
  'done.leadPaid': 'Your {plan} plan is now active and your payment has been received.',
  'done.paidToday': 'Paid today',
  'done.mail': 'These details are also sent by e-mail to {email}.',
  'done.cancelNote': 'You can cancel your subscription at any time from the Plans screen; after cancelling you keep access until the end of the period.',
  'done.watch': 'Start watching',
  'done.manage': 'Manage subscription',
  'done.noneTitle': 'No completed purchase was found',
  'done.noneBody': 'You do not appear to have an active subscription. You can choose a plan on the Plans screen.',
  'done.toPlans': 'Go to Plans',
  'done.refLabel': 'Transaction no.',

  // --- Settings --------------------------------------------------------------
  'settings.title': 'Settings',
  'settings.password.current': 'Current password',
  'settings.password.new': 'New password',
  'settings.password.newPlaceholder': 'At least 8 characters',
  'settings.password.note': 'When you change your password, sessions on other devices are signed out; this device stays signed in.',
  'settings.password.title': 'Change password',
  'settings.password.confirm': 'Change',
  'settings.password.fail': 'The password could not be changed.',
  'settings.delete.placeholder': 'Your password',
  'settings.delete.body1': 'Your account, sources, favourites, watch history and device records will be deleted. This cannot be undone.',
  'settings.delete.body2': 'Copies in daily backups are removed within 14 days at the latest.',
  'settings.delete.label': 'Enter your password to confirm',
  'settings.delete.title': 'Permanently delete account',
  'settings.delete.confirm': 'Delete my account',
  'settings.delete.fail': 'The account could not be deleted.',
  'settings.logout': 'Sign out',
  'settings.deleteBtn': 'Delete my account',
  'settings.passBtn': 'Change password',
  'settings.subscription': 'Subscription',
  'settings.subLead': 'Plan changes and cancellations are made on the Plans screen.',
  'settings.package': 'Plan',
  'settings.dailyTime': 'Daily YouTube allowance',
  'settings.dailyValue': '{n} minutes · resets at midnight ({tz})',
  'settings.dailyNone': 'No daily limit applies',
  'settings.plansRow': 'Plans',
  'settings.plansRowMeta': 'See the plans, your subscription and your transaction history.',
  'settings.plansRowBtn': 'Go to Plans',
  'settings.security': 'Security',
  'settings.securityLead': 'When you change your password, sessions on other devices are signed out. If you have forgotten your password, you can reset it by e-mail using the “Forgot your password?” link on the sign-in screen.',
  'settings.legalLead': 'The terms, privacy and subscription texts are on the public legal page.',
  'settings.deleteSection': 'Delete account',
  'settings.deleteLead': 'Your account and all of your data are permanently deleted: sources, favourites, watch history and device records. This cannot be undone.',

  // --- Sources ---------------------------------------------------------------
  'sources.title': 'My sources',
  'sources.loadFail': 'Sources could not be loaded',
  'sources.lockedLead': 'Adding your own M3U or Xtream list is available on the Plus plan. The Free and Starter plans do not include IPTV.',
  'sources.lockedTitle': 'Connect your own source with Plus',
  'sources.lockedBody': 'Once you add a source, your channels, movies and series are gathered in the IPTV section.',
  'sources.count.live': 'channels',
  'sources.count.movie': 'movies',
  'sources.count.series': 'series',
  'sources.count.episode': 'episodes',
  'sources.sync': 'Refresh',
  'sources.synced': 'List updated.',
  'sources.syncFail': 'Refresh failed: {error} Your previous list was kept.',
  'sources.delete': 'Delete',
  'sources.deleteTitle': 'Delete source',
  'sources.deleteBody': '“{name}” and the catalogue from this source will be deleted. Favourites from this source are removed as well.',
  'sources.deleted': 'Source deleted.',
  'sources.updated': 'Updated {time}',
  'sources.lastFail': 'Last refresh failed: {error}',
  'sources.notSynced': 'Not refreshed yet',
  'sources.goChannels': 'Go to channels',
  'sources.slotsFull': 'All source slots are in use. Delete an existing source to add a new one.',
  'sources.addOnPhone': 'Add from your phone',
  'sources.anyway': 'Enter it here anyway',
  'sources.phoneTitle': 'Add your source from your phone',
  'sources.phoneBody': 'An M3U address or Xtream username and password is long and hard to type on a car display. Add it from your phone and your channels will appear here.',
  'sources.addNew': 'Add new source',
  'sources.lead': 'Your plan includes {n} source slots. Content comes from the provider you add; no channel or content packages are sold.',
  'sources.emptyTitle': 'Add your playlist to start watching',
  'sources.emptyBody': 'Connect your own source with an M3U address or Xtream server details.',
  'sources.form.xtream': 'Xtream — server, username, password',
  'sources.form.m3u': 'M3U — playlist link',
  'sources.form.namePlaceholder': 'Home list',
  'sources.form.m3uLabel': 'M3U address',
  'sources.form.m3uHint': 'The direct link to the playlist. It must start with http or https.',
  'sources.form.epgLabel': 'Programme guide address',
  'sources.form.epgHint': 'Optional. If the source supports it, now/next information is loaded from here.',
  'sources.form.hostLabel': 'Server address',
  'sources.form.hostHint': 'Address and port only; do not enter the username here.',
  'sources.form.userLabel': 'Username',
  'sources.form.userPlaceholder': 'username',
  'sources.form.passLabel': 'Password',
  'sources.form.passHint': 'Your password is stored encrypted and never written to logs.',
  'sources.form.test': 'Test connection',
  'sources.form.save': 'Save and fetch content',
  'sources.form.testing': 'Testing…',
  'sources.form.saving': 'Saving and fetching content. Large lists may take a moment.',
  'sources.form.added': 'Source added. Your channels are in the IPTV section.',
  'sources.form.title': 'New source',
  'sources.form.lead': 'Your provider supplies these details. You can test the connection before saving.',
  'sources.form.typeLabel': 'Type',
  'sources.form.nameLabel': 'Name',

  // --- Devices ---------------------------------------------------------------
  'devices.title': 'Devices',
  'devices.lead': 'You can sign in on as many devices as you like; only one of them can stream at a time.',
  'devices.none': 'No registered devices',
  'devices.thisDevice': 'this device',
  'devices.lastSeen': 'Last seen {time} · open sessions: {n}',
  'devices.signOutHere': 'Sign out of this device',
  'devices.remove': 'Remove device',
  'devices.removed': 'Device session ended.',

  // --- Support ---------------------------------------------------------------
  'support.title': 'Support information',
  'support.lead': 'Share these details when reporting a problem.',
  'support.build': 'Application build',
  'support.webcodecs': 'WebCodecs',
  'support.supported': 'Supported',
  'support.unsupported': 'Not supported — playback will not work in this browser',
  'support.deviceLabel': 'Device label',
  'support.browser': 'Browser',
  'support.clip': 'Play diagnostic clip',
  'support.clipTitle': 'Diagnostic clip',
  'support.clipRow': 'Playback diagnostic clip ',
  'support.clipMeta': 'The on-screen seconds counter and the audio beep should coincide.',

  // --- Compatibility ---------------------------------------------------------
  'compat.title': 'Is your device supported?',
  'compat.ok': 'This browser has the support required for playback.',
  'compat.bad': 'Playback does not work in this browser. Please use a current version of Chrome, Edge or Safari.',
  'compat.lead': 'The app runs in the browser; no installation is needed. The interface adapts to the screens of the devices below.',
  'compat.pc': 'Current Chrome, Edge or Safari · keyboard and mouse',
  'compat.phone': 'Portrait layout · touch controls',
  'compat.car': 'Large-button car layout · long text entry is handed off to the phone',
  'compat.tablet': 'Wide-screen layout · touch controls',
  'compat.tv': 'Large, readable type and layout for viewing from a distance',
  'compat.foot': 'Playback requires a current browser version. In an unsupported browser the app says so clearly; it does not show a blank screen.',

  // --- Sign in / register ----------------------------------------------------
  'auth.titleLogin': 'Welcome back.',
  'auth.titleRegister': 'Create your account.',
  'auth.leadLogin': 'Your lists and where you left off follow you to every device.',
  'auth.leadRegister': 'Creating an account is free. You can watch the built-in channels with no time limit and YouTube videos for 30 minutes a day.',
  'auth.name': 'Name (optional)',
  'auth.namePlaceholder': 'Your name',
  'auth.email': 'E-mail',
  'auth.emailPlaceholder': 'name@example.com',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'Your password',
  'auth.passwordNew': 'At least 8 characters',
  'auth.consent.terms': 'Terms of Use',
  'auth.consent.kvkk': 'Privacy Notice (KVKK)',
  'auth.consent.and': ' and the ',
  'auth.consent.tail': ' — I have read and accept both. I confirm that I am 18 or older.',
  'auth.login': 'Sign in',
  'auth.register': 'Create account',
  'auth.switchToRegister': 'Create an account',
  'auth.switchToLogin': 'Sign in',
  'auth.noAccount': 'Don’t have an account? ',
  'auth.haveAccount': 'Already have an account? ',
  'auth.consentRequired': 'To continue, you need to accept the terms and the privacy notice.',
  'auth.fail': 'The request could not be completed.',
  'auth.forgot': 'If you have forgotten your password, write to the address in the {contact} section.',
  'auth.forgotLink': 'Forgot your password?',
  'auth.reset.titleForgot': 'Reset your password',
  'auth.reset.leadForgot': 'Enter the e-mail address of your account; a link to set a new password will be sent to you.',
  'auth.reset.send': 'Send the link',
  'auth.reset.remembered': 'Remembered your password? ',
  'auth.reset.validFor': 'The link is valid for {n} minutes. If the message does not arrive within a few minutes, please check your spam folder.',
  'auth.reset.titleNew': 'Set a new password',
  'auth.reset.leadNew': 'Enter your new password. When you save it, you are signed out on all devices.',
  'auth.reset.newPassword': 'New password',
  'auth.reset.save': 'Save the password',
  'auth.reset.missing': 'This link is invalid. Please request a new reset link.',
  'auth.reset.requestNew': 'Request a new link',
  'auth.visualTitle': 'Wherever you want to watch.',
  'auth.visualNote': 'Sample illustration; not an actual catalogue image.',
  'auth.car.drive': 'Do not watch video while driving. This screen is for passengers.',

  // --- Handoff to phone ------------------------------------------------------
  'handoff.plan.title': 'Buy the plan from your phone',
  'handoff.plan.lead': 'The payment step is not suited to a car display. Go to this address on your phone, sign in with the same account and choose the plan there. The car display updates automatically.',
  'handoff.plan.step3': 'Choose the plan and complete the payment.',
  'handoff.plan.step3plus': 'Choose the Plus plan and complete the payment.',
  'handoff.plan.fallback': 'Continue here anyway',
  'handoff.source.title': 'Add your source from your phone',
  'handoff.source.lead': 'An M3U address or Xtream username and password is long and hard to type on a car display. Add it from your phone and your channels will appear here.',
  'handoff.source.step3': 'Settings → My sources → Add new source.',
  'handoff.source.fallback': 'Enter it here anyway',
  'handoff.step1': 'Open the browser on your phone and type the address above.',
  'handoff.step2': 'Sign in with the same e-mail.',
  'handoff.url': 'Go to this address on your phone',
  'handoff.back': 'Go back',

  // --- Discover / search / live / IPTV / list --------------------------------
  'locked.alt': 'Sample interface image',
  'locked.tag': 'Plus plan',
  'locked.title1': 'Your own list.',
  'locked.title2': 'Organised your way.',
  'locked.body': '{what} come from your own M3U or Xtream source. No channel or content packages are sold.',
  'locked.stateTitle': 'Add your own source with Plus',
  'locked.stateBody': 'Bring your channels, movies and series together; return to your favourites and continue where you left off. The Free and Starter plans do not include IPTV.',
  'discover.title': 'What would you like to watch today?',
  'discover.subtitle': 'Find something new, tune in live or pick up where you left off.',
  'discover.loading': 'Getting things ready to watch…',
  'discover.continue': 'Continue watching',
  'discover.goList': 'Go to My List',
  'discover.shelf': 'Discover',
  'discover.emptyTitle': 'Discover is empty right now',
  'discover.emptyBody': 'The content provider could not be reached. Search may still work.',
  'discover.loadFail': 'Discover could not be loaded',
  'discover.moreFail': 'More items could not be loaded.',
  'discover.mixed': 'Mixed',
  'discover.liveNow': 'Live now',
  'discover.watch': 'Watch',
  'discover.liveStrip': 'Live channels',
  'discover.allChannels': 'All channels',
  'search.placeholder': 'Video, channel or movie title',
  'search.title': 'Search',
  'search.recent': 'Your recent searches',
  'search.clear': 'Clear',
  'search.emptyTitle': 'What would you like to find?',
  'search.emptyBody': 'Type a video, channel or movie title in the field above.',
  'search.searching': 'Searching…',
  'search.videos': 'Videos',
  'search.mylist': 'My IPTV list',
  'search.countMine': 'Results: {n} · my IPTV list',
  'search.noMatchTitle': 'No matches in your IPTV list',
  'search.noMatchBody': 'Try a different title, or refresh your source from Settings.',
  'search.count': 'Results: {n}',
  'search.noResultTitle': 'No results found',
  'search.noResultBody': 'Check the spelling or try a shorter phrase.',
  'search.loadMoreN': 'Show more ({n})',
  'search.end': 'End of list.',
  'search.fail': 'Results could not be loaded',
  'live.title': 'Live',
  'live.subIptv': 'Built-in channels and your own list',
  'live.subFree': 'Built-in channels. Your own list is available with Plus.',
  'live.channelSearch': 'Search channels',
  'live.builtin': 'Built-in channels',
  'live.noMatch': 'No matching channels',
  'live.noMatchBody': 'Clear the search and try again.',
  'live.listFail': 'The channel list could not be loaded',
  'live.listFailBody': 'Check your connection and try again.',
  'live.mine': 'My IPTV list',
  'live.mineLockedTitle': 'Your own channels require Plus',
  'live.mineLockedBody': 'Add your M3U or Xtream source to bring your own channels here.',
  'live.seePlus': 'See Plus',
  'live.goIptv': 'Go to IPTV',
  'iptv.title': 'IPTV',
  'iptv.lockedWhat': 'IPTV channels, movies and series',
  'iptv.tabChannels': 'Channels',
  'iptv.tabMovies': 'Movies',
  'iptv.tabSeries': 'Series',
  'iptv.sync': 'Refresh',
  'iptv.addFirst': 'Add a source in Settings first.',
  'iptv.noChannelTitle': 'No channels in this list',
  'iptv.noChannelBody': 'Try refreshing your source or choosing another category.',
  'iptv.showing': 'Showing {shown} of {total} channels. Choose a category to narrow the list.',
  'iptv.channelsFail': 'Channels could not be loaded',
  'iptv.noMovies': 'No movies in this list',
  'iptv.noSeries': 'No series in this list',
  'iptv.noKindBody': 'Your source may not offer this type of content. Try refreshing the list from Settings.',
  'iptv.contentFail': 'Content could not be loaded',
  'series.title': 'Series',
  'series.lockedWhat': 'Series',
  'series.noEpisodesSeason': 'No episodes in this season',
  'series.season': 'Season {n}',
  'series.playFirst': 'Play first episode',
  'series.back': 'Back to series',
  'series.tag': 'Series',
  'series.noEpisodesTitle': 'No episode list',
  'series.noEpisodesBody': 'The provider did not supply episode information for this series.',
  'series.fail': 'The series could not be opened',
  'library.title': 'My List',
  'library.tabContinue': 'Continue',
  'library.tabFavorites': 'Favourites',
  'library.noFavTitle': 'No favourites yet',
  'library.noFavBody': 'Tap the star on a card to add a channel, video or movie.',
  'library.noContinueTitle': 'Nothing in progress',
  'library.noContinueBody': 'Once you start watching a video or movie, you can continue from here.',
  'library.fail': 'My List could not be loaded',

  // --- Cards -----------------------------------------------------------------
  'cards.noImage': 'No image',
  'cards.addFav': 'Add to My List',
  'cards.removeFav': 'Remove from My List',
  'cards.favFail': 'Could not save to My List.',
  'cards.live': 'LIVE',
  'cards.noEpg': 'No programme information',
  'cards.next': 'Next: {title}',
  'cards.left': '{time} left',
  'cards.watched': 'Watched',
  'cards.kind.youtubeVod': 'Video',
  'cards.kind.youtubeLive': 'Live stream',
  'cards.kind.tv': 'Live channel',
  'cards.kind.iptvLive': 'IPTV · channel',
  'cards.kind.iptvMovie': 'Movie',
  'cards.kind.iptvEpisode': 'Episode',
  'cards.kind.iptvSeries': 'Series',
  'cards.kind.test': 'Diagnostic clip',
  'cards.kind.content': 'Content',
  'cards.episodeSub': 'Season {season}, episode {episode}',

  // --- Feedback components ---------------------------------------------------
  'feedback.confirm': 'Continue',
  'feedback.cancel': 'Cancel',
  'notFound.title': 'Page not found',
  'notFound.body': 'The screen you are looking for may have moved.',

  // --- Player ----------------------------------------------------------------
  'player.noWebCodecs': 'This browser cannot play video here. Try a current version of Chrome, Edge or Safari.',
  'player.connecting': 'Connecting…',
  'player.preparing': 'Preparing stream…',
  'player.openFail': 'This stream cannot be opened right now. Please try again.',
  'player.activeTitle': 'A stream is open on another device',
  'player.activeBody': 'Your account allows one stream at a time. “{title}” is currently playing on {device}. Stop the other device and watch here?',
  'player.activeDefaultTitle': 'an item',
  'player.activeDefaultDevice': 'another device',
  'player.activeConfirm': 'Watch here',
  'player.busy': 'The server is busy right now. Please try again in a few minutes.',
  'player.preemptedTitle': 'The server is busy right now',
  'player.preemptedBody': 'This stream was closed because capacity is full. You can open it again shortly.',
  'player.takenTitle': 'Playback moved to another device',
  'player.takenBody': 'This stream was stopped because playback started on another device on your account.',
  'player.entitlementLost': 'The IPTV stream was closed because your plan entitlement has ended.',
  'player.stoppedTitle': 'Stream stopped',
  'player.stoppedBody': 'The stream ended on the server side. Go back and open it again.',
  'player.seekFail': 'Seeking failed. Please try again.',
  'player.seeking': 'Seeking…',
  'player.cantOpen': 'This stream cannot be opened',
  'player.quotaTitle': 'Your free YouTube time for today is used up',
  'player.quotaBody': 'The Free plan includes a total of 30 minutes of YouTube playback per day. Your position has been saved; you can continue from here once you upgrade to a paid plan. Built-in channels remain available with no time limit.',
  'player.plusTitle': 'This content requires the Plus plan',
  'player.goPlus': 'Upgrade to Plus',
  'player.drive': 'Do not watch video while driving. This screen is for passengers; looking at the screen in a moving vehicle is dangerous and may be illegal.',
  'player.quotaSoon': 'Your free YouTube time for today ends in one minute. Your position will be saved.',
  'player.decodeErr': 'This stream cannot be played on this device. Try another stream.',
  'player.parseErr': 'This stream cannot be played on this device. Try another stream.',
  'player.noTrack': 'This stream has no video.',
  'player.unsupported': 'This stream cannot be played on this device. Try another stream.',
  'player.streamFail': 'The video stream could not be opened.',
  'player.ctl.back': 'Back',
  'player.ctl.close': 'Close (Esc)',
  'player.ctl.play': 'Play',
  'player.ctl.pause': 'Pause',
  'player.ctl.playTitle': 'Play / pause (space)',
  'player.ctl.back10': '10 seconds back',
  'player.ctl.back10Title': '10 seconds back (left arrow)',
  'player.ctl.fwd10': '10 seconds forward',
  'player.ctl.fwd10Title': '10 seconds forward (right arrow)',
  'player.ctl.channels': 'Channel list',
  'player.ctl.quality': 'Video quality',
  'player.ctl.qualityLabel': 'Quality {q}p',
  'player.ctl.fullscreen': 'Full screen',
  'player.ctl.fullscreenTitle': 'Full screen (F)',
  'player.ctl.mute': 'Mute',
  'player.ctl.unmute': 'Unmute',
  'player.ctl.volume': 'Volume',
  'player.ctl.position': 'Position',
  'player.live': 'LIVE',
  'player.skipSec': '{n} s',
  'player.rotate': 'Turn your phone sideways for a larger picture',
  'player.fullscreenUnsupported': 'This browser does not allow full screen.',
  'player.teslaFullTitle': 'Full screen requires Theatre mode',
  'player.teslaFullBody': 'The Tesla browser only allows pages to go full screen in Theatre mode. If you continue, the page reopens in full screen through a YouTube redirect; on the page that opens, confirm that you want to continue to the site. Your vehicle must be in Park. Your position is saved.',
  'player.teslaFullConfirm': 'Open in full screen',
  'player.netSlow': 'Your internet connection appears slow or unstable. Playback may freeze.',
  'player.netSlowQuality': 'Your internet connection appears slow or unstable. Playback may freeze; you may select a lower quality.',
  'playback.related': 'Watch next',
  'playback.relatedSub': 'Similar content from the same channel',
  'playback.backToList': 'Back to list',
  'playback.noRelated': 'No suggestions found.',
  'playback.relatedFail': 'Suggestions could not be loaded.',
  'player.reconnecting': 'Reconnecting…',
  'player.lostTitle': 'Connection lost',
  'player.lostBody': 'The stream cannot be reached. Check your internet connection and try again.',
  'boot.offlineTitle': 'Cannot reach the server',
  'boot.offlineBody': 'Check your internet connection. The page opens automatically once the connection is back, or you can try again now.',
};
